import { Box, Divider, Stack, TextField, Typography, useTheme } from "@mui/material";
import React from "react";
import { Autocomplete, AutocompleteOption } from "src/components";
import { useSession } from "src/hooks";
import { useGetTeams } from "src/http";
import { ITeamSummary } from "src/models";

interface IProps {
  placeholder?: string;
  value: ITeamSummary | null;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  autofocus?: boolean;
  groupByTeamMembership?: boolean;
  disableOptionsBasedOnMembership?: boolean;
  optionsFilter?: (team: ITeamSummary) => boolean;
  onBlur?: () => void;
  renderOptionSubtext?: (team: ITeamSummary) => React.ReactNode;
  onChange: (value: ITeamSummary | null) => void;
}

const TeamAutocomplete = React.forwardRef((props: IProps, ref: React.Ref<unknown>) => {
  const { userId: sessionUserId } = useSession();
  const { data: teams, isLoading } = useGetTeams(sessionUserId);
  const theme = useTheme();

  function sortFunction(a: ITeamSummary, b: ITeamSummary) {
    const aMembership = a.isAdministrator || a.isMember;
    const bMembership = b.isAdministrator || b.isMember;

    if (aMembership !== bMembership) {
      return aMembership ? -1 : 1;
    }

    return a.name.localeCompare(b.name);
  }

  function getOptions() {
    let options = teams ?? [];
    if (props.optionsFilter) {
      options = options.filter(props.optionsFilter);
    }

    if (props.groupByTeamMembership) {
      options = options.sort(sortFunction);
    }
    return options;
  }

  return (
    <Autocomplete
      ref={ref}
      {...props}
      isLoading={isLoading}
      disabled={isLoading || teams == null || props.disabled}
      options={getOptions()}
      groupBy={
        props.groupByTeamMembership
          ? (option) => (option.isMember || option.isAdministrator ? "My Teams" : "Other Teams")
          : undefined
      }
      getOptionLabel={(option) => option.name}
      getOptionDisabled={(option) =>
        props.disableOptionsBasedOnMembership ? !(option.isMember || option.isAdministrator) : false
      }
      renderGroup={
        props.groupByTeamMembership
          ? (group) => (
              <Box key={group.key} sx={{ pb: 0.25 }}>
                <Stack
                  spacing={0.2}
                  sx={{
                    px: 0.5,
                    pt: 0.75,
                    position: "sticky",
                    top: 0,
                    backgroundColor: theme.palette.common.white,
                    zIndex: 1,
                  }}
                >
                  <Typography variant="caption">{group.group.toLocaleUpperCase()}</Typography>
                  <Divider />
                </Stack>
                {group.children}
              </Box>
            )
          : undefined
      }
      onBlur={props.onBlur}
      onChange={(_, value) => {
        const selection = teams?.find((x) => x.slug === value?.slug);
        props.onChange?.(selection ?? null);
      }}
      renderOption={(liProps, option) => (
        <AutocompleteOption {...liProps} key={option.slug}>
          <Stack>
            <Typography>{option.name}</Typography>
            {props.disableOptionsBasedOnMembership && !option.isMember && !option.isAdministrator && (
              <Typography variant="caption">You are not a member or administrator of this team</Typography>
            )}
          </Stack>
        </AutocompleteOption>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={props.placeholder}
          error={props.error}
          helperText={props.helperText}
          autoFocus={props.autofocus}
        />
      )}
      blurOnSelect={false}
      isOptionEqualToValue={(option, value) => option.slug === value.slug}
      value={props.value}
    />
  );
});

export { TeamAutocomplete };
